/**
 * @since 2026-04-18
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import React from "react";
import { Box } from "@mui/material";
import EChart from "components/Shared/EChart";
import ChartCard from "components/Shared/ChartCard";

const fmtPrice = (v) =>
  v == null ? "—" : Math.abs(v) >= 100 ? v.toFixed(2) : v.toFixed(4);

const ROLL_LINE_COLOR = "#ff9800";
const FRONT_COLOR = "#90caf9";
const BACK_COLOR = "#f48fb1";

const FuturesRollPanel = ({ detail }) => {
  if (!detail) return null;
  const { rollZooms = [], rollDifferences = [] } = detail;

  const diffOption = {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "line" },
      formatter: (params) => {
        if (!params || !params.length) return "";
        const p = params[0];
        if (!p.value || p.value[1] == null) return "";
        const d = String(p.value[0]).slice(0, 10);
        return `${d}<br/>Roll diff: ${fmtPrice(Number(p.value[1]))}`;
      },
    },
    grid: { left: 70, right: 30, top: 20, bottom: 30 },
    xAxis: { type: "time" },
    yAxis: { type: "value", scale: true, axisLabel: { formatter: (v) => v.toFixed(2) } },
    series: [
      {
        name: "Roll difference",
        type: "bar",
        barWidth: 2,
        data: rollDifferences,
        itemStyle: { color: (p) => (p.value && p.value[1] >= 0 ? "#4caf50" : "#f44336") },
      },
    ],
  };

  const rollMarkLine = (rollDateIso, { showLabel = false } = {}) => ({
    silent: true,
    symbol: "none",
    lineStyle: { color: ROLL_LINE_COLOR, type: "solid", width: 1.5 },
    label: showLabel
      ? {
          show: true,
          formatter: String(rollDateIso).slice(0, 10),
          position: "insideEndTop",
          color: ROLL_LINE_COLOR,
          fontSize: 11,
          fontWeight: 600,
        }
      : { show: false },
    data: [{ xAxis: rollDateIso }],
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {rollDifferences.length > 0 && (
        <ChartCard title="Roll differences (NotAdj_t − NotAdj_{t-1})">
          <EChart option={diffOption} height={200} />
        </ChartCard>
      )}

      {rollZooms.length === 0 ? (
        <Box sx={{ opacity: 0.7 }}>No roll dates available or no adjusted series.</Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 2 }}>
          {rollZooms.map((zoom, i) => {
            const rollDate = zoom.date;
            const priceOption = {
              animation: false,
              tooltip: { trigger: "axis" },
              legend: { top: 0, right: 8, textStyle: { fontSize: 11 } },
              grid: { left: 55, right: 15, top: 28, bottom: 6, height: 130 },
              xAxis: { type: "time", axisLabel: { show: false } },
              yAxis: { type: "value", scale: true, name: "Close", nameTextStyle: { fontSize: 10 } },
              series: [
                {
                  name: zoom.frontContract || "Front",
                  type: "line",
                  showSymbol: true,
                  symbolSize: 4,
                  data: zoom.frontClose || [],
                  lineStyle: { width: 1.5, color: FRONT_COLOR },
                  itemStyle: { color: FRONT_COLOR },
                  markLine: rollMarkLine(rollDate, { showLabel: true }),
                },
                {
                  name: zoom.backContract || "Back",
                  type: "line",
                  showSymbol: true,
                  symbolSize: 4,
                  data: zoom.backClose || [],
                  lineStyle: { width: 1.5, color: BACK_COLOR },
                  itemStyle: { color: BACK_COLOR },
                },
              ],
            };
            const volumeOption = {
              animation: false,
              tooltip: { trigger: "axis" },
              legend: { show: false },
              grid: { left: 55, right: 15, top: 8, bottom: 28, height: 70 },
              xAxis: { type: "time" },
              yAxis: { type: "value", scale: true, name: "Volume", nameTextStyle: { fontSize: 10 } },
              series: [
                {
                  name: zoom.frontContract || "Front",
                  type: "bar",
                  stack: "volume",
                  data: zoom.frontVolume || [],
                  itemStyle: { color: FRONT_COLOR },
                  markLine: rollMarkLine(rollDate),
                },
                {
                  name: zoom.backContract || "Back",
                  type: "bar",
                  stack: "volume",
                  data: zoom.backVolume || [],
                  itemStyle: { color: BACK_COLOR },
                },
              ],
            };
            const dateStr = rollDate?.slice(0, 10);
            const missingFront = zoom.frontHasPriceOnRoll === false;
            const missingBack = zoom.backHasPriceOnRoll === false;
            const flagText = missingFront && missingBack
              ? "no price either side"
              : missingFront
              ? "no front price"
              : missingBack
              ? "no back price"
              : null;
            const title = (
              <Box component="span" sx={{ display: "inline-flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
                <Box component="span">{dateStr}</Box>
                <Box component="span" sx={{ opacity: 0.7, fontSize: 12 }}>
                  · {zoom.frontContract || "?"} → {zoom.backContract || "?"}
                </Box>
                {flagText && (
                  <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: "warning.main" }}>
                    · {flagText}
                  </Box>
                )}
              </Box>
            );
            return (
              <ChartCard key={i} title={title}>
                <EChart option={priceOption} height={170} />
                <EChart option={volumeOption} height={120} />
              </ChartCard>
            );
          })}
        </Box>
      )}

    </Box>
  );
};

export default FuturesRollPanel;
